import { useState } from 'react';
import { Snackbar, Alert } from '@mui/material';

// Helper function to migrate old privileges structure
const migratePrivilegesStructure = (oldPrivileges) => {
  // If already in new format, return as is
  if (oldPrivileges.tabVisibility) {
    return oldPrivileges;
  }
  
  // Migrate from old structure
  const migrated = {
    tabVisibility: {
      home: true,
      appUsers: false,
      schemes: false,
      systemUsers: false,
      userGroups: false,
      masters: false,
      reports: false,
      auditLogs: false
    },
    appUsers: oldPrivileges.userPermissions || {},
    appUserDetails: oldPrivileges.userDetailsPermissions || {},
    schemes: oldPrivileges.yojnaPermissions || {},
    systemUsers: {},
    masters: {},
    reports: {}
  };
  
  // Set tab visibility based on permissions
  if (migrated.appUsers.read || migrated.appUsers.write) {
    migrated.tabVisibility.appUsers = true;
  }
  if (migrated.schemes.read || migrated.schemes.write) {
    migrated.tabVisibility.schemes = true;
  }
  
  return migrated;
};

export const usePermissions = () => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error'
  });

  const showSnackbar = (message, severity = 'error') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const hideSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  const checkPermission = (requiredPermission, action) => {
    const userPrivileges = JSON.parse(localStorage.getItem('userPrivileges') || '{}');
    const userGroupName = localStorage.getItem('userGroupName') || '';
    
    // Super-admin has access to everything
    if (userGroupName.toLowerCase() === 'super-admin' || userGroupName.toLowerCase() === 'superadmin') {
      return true;
    }
    
    // Migrate old structure if needed
    const privileges = migratePrivilegesStructure(userPrivileges);
    
    switch (requiredPermission) {
      case 'USER':
      case 'APP_USERS':
        return privileges?.appUsers?.[action] || false;
      case 'USER_DETAILS':
      case 'APP_USER_DETAILS':
        return privileges?.appUserDetails?.[action] || false;
      case 'YOJNA':
      case 'SCHEMES':
        return privileges?.schemes?.[action] || false;
      case 'SYSTEM_USERS':
        return privileges?.systemUsers?.[action] || false;
      case 'MASTERS':
        return privileges?.masters?.[action] || false;
      case 'REPORTS':
        return privileges?.reports?.[action] || false;
      default:
        return false;
    }
  };

  // Check if a tab should be visible
  const canViewTab = (tabName) => {
    const userGroupName = localStorage.getItem('userGroupName') || '';
    
    // Super-admin can see all tabs
    if (userGroupName.toLowerCase() === 'super-admin' || userGroupName.toLowerCase() === 'superadmin') {
      return true;
    }
    
    const userPrivileges = JSON.parse(localStorage.getItem('userPrivileges') || '{}');
    const privileges = migratePrivilegesStructure(userPrivileges);
    
    return privileges?.tabVisibility?.[tabName] || false;
  };

  const handlePermissionAction = (permission, action, callback) => {
    if (checkPermission(permission, action)) {
      return callback();
    }
    
    showSnackbar('You do not have permission to perform this action');
    return false;
  };

  const canViewUserDetail = (field) => {
    const userGroupName = localStorage.getItem('userGroupName') || '';
    
    // Super-admin can view all user details
    if (userGroupName.toLowerCase() === 'super-admin' || userGroupName.toLowerCase() === 'superadmin') {
      return true;
    }
    
    const userPrivileges = JSON.parse(localStorage.getItem('userPrivileges') || '{}');
    return userPrivileges?.userDetailsPermissions?.[field] || false;
  };

  const maskSensitiveData = (data) => {
    const userGroupName = localStorage.getItem('userGroupName') || '';
    
    // Super-admin can view all data
    if (userGroupName.toLowerCase() === 'super-admin' || userGroupName.toLowerCase() === 'superadmin') {
      return data;
    }
    
    if (!checkPermission('USER', 'read')) {
      return '***';
    }
    return data;
  };

  // True if user can use direct yojana create/update/delete (admin). Otherwise use approval endpoints.
  const isYojanaAdmin = () => {
    const userGroupName = localStorage.getItem('userGroupName') || '';
    return userGroupName.toLowerCase() === 'super-admin' || userGroupName.toLowerCase() === 'superadmin';
  };

  return {
    checkPermission,
    handlePermissionAction,
    canViewUserDetail,
    maskSensitiveData,
    canViewTab,
    isYojanaAdmin,
    snackbar,
    hideSnackbar
  };
}; 